// Chart mode's queries (M1-08): the org chart (seats with holders, edges, teams and members in
// one call; the editor, the TEAMS sidebar and the seat page share it), the teams list and one
// team (Setup › Teams). The money overlay reads M5-03's budget vs actual (M5-04,
// features/finance/finance-queries.ts) instead of paging the cost ledger. Keys sit under the
// company's, so change pings (live/) refresh them with everything else of the company.
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { PAGE } from "../queries";

export const chartKeys = {
  orgChart: (co: string) => ["company", co, "org-chart"] as const,
  /** CHART-DIMENSIONS: the seat dimensions of a week (one call for every card). */
  dimensions: (co: string, week: string) =>
    ["company", co, "org-chart", "dimensions", week] as const,
  /**
   * KPIS-MODE: the seats whose KPI data the caller may see (`GET org-chart/kpi-visibility`,
   * `kpi_visible_seat_ids`): the KPIs sidebar's seat tree and the scorecard grid's rows.
   */
  kpiVisibility: (co: string) => ["company", co, "org-chart", "kpi-visibility"] as const,
  teams: (co: string) => ["company", co, "teams"] as const,
  team: (co: string, id: string) => ["company", co, "teams", "detail", id] as const,
};

export const orgChartQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: chartKeys.orgChart(co),
    queryFn: () => api.client.orgChart.get({ params: { co } }),
  });

export const orgChartDimensionsQuery = (api: Api, co: string, week: string) =>
  queryOptions({
    queryKey: chartKeys.dimensions(co, week),
    queryFn: () => api.client.orgChart.dimensions({ params: { co }, query: { period: week } }),
  });

/**
 * KPIS-MODE: the one server answer to "whose KPIs may I see" (the chart's `kpi` audience; the
 * KPI-VISIBILITY package widens it on the server only). Never decided on the client.
 */
export const kpiVisibilityQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: chartKeys.kpiVisibility(co),
    queryFn: () => api.client.orgChart.kpiVisibility({ params: { co } }),
    staleTime: 60_000,
  });

export const teamsQuery = (api: Api, co: string) =>
  infiniteQueryOptions({
    queryKey: chartKeys.teams(co),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.teams.list({
        params: { co },
        query: { limit: PAGE.setup, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export const teamQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: chartKeys.team(co, id),
    queryFn: () => api.client.teams.get({ params: { co, id } }),
  });
